import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	COLORS,
	CONDITION_SLIDER,
	DYED_GRADIENT,
	TRANSPARENT_PATTERN,
} from "#/lib/constants";
import {
	type FilterState,
	flightRangeLabel,
	priceRangeLabel,
	signedVal,
	weightRangeLabel,
} from "#/lib/listingFilters";

type ActivePillsProps = {
	filters: FilterState;
	onChange: (next: Partial<FilterState>) => void;
};

type Pill = {
	id: string;
	label: string;
	colorDot?: string | "dyed" | "transparent";
	onRemove: () => void;
};

function Pill({ label, colorDot, onRemove }: Omit<Pill, "id">) {
	return (
		<span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/8 px-2.5 py-0.5 text-xs font-medium text-primary-accessible">
			{colorDot && (
				<span
					aria-hidden
					className="block size-2.5 shrink-0 rounded-full border border-black/10"
					style={
						colorDot === "dyed"
							? { background: DYED_GRADIENT }
							: colorDot === "transparent"
								? { background: TRANSPARENT_PATTERN }
								: { backgroundColor: colorDot }
					}
				/>
			)}
			{label}
			<button
				type="button"
				aria-label={`Remove ${label} filter`}
				onClick={onRemove}
				className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
			>
				<X className="size-2.5" />
			</button>
		</span>
	);
}

export function ActivePills({ filters, onChange }: ActivePillsProps) {
	const { t } = useTranslation("browse");
	const pills: Pill[] = [];

	if (filters.q) {
		pills.push({
			id: "q",
			label: `${t("activePills.search")} "${filters.q}"`,
			onRemove: () => onChange({ q: "" }),
		});
	}

	for (const val of filters.discClass) {
		pills.push({
			id: `discClass-${val}`,
			label: t(`discClasses.${val}`, { defaultValue: val }),
			onRemove: () =>
				onChange({ discClass: filters.discClass.filter((v) => v !== val) }),
		});
	}

	if (filters.condition.length > 0) {
		const indices = filters.condition
			.map((c) => CONDITION_SLIDER.findIndex((s) => s.value === c))
			.filter((i) => i >= 0)
			.sort((a, b) => a - b);
		const loTier = CONDITION_SLIDER[indices[0]];
		const hiTier = CONDITION_SLIDER[indices[indices.length - 1]];
		const lo = loTier ? t(`conditions.${loTier.value}`) : filters.condition[0];
		const hi = hiTier
			? t(`conditions.${hiTier.value}`)
			: filters.condition[filters.condition.length - 1];
		const condLabel =
			lo === hi
				? `${t("activePills.condition")}: ${lo}`
				: `${t("activePills.condition")}: ${lo} – ${hi}`;
		pills.push({
			id: "condition-range",
			label: condLabel,
			onRemove: () => onChange({ condition: [] }),
		});
	}

	for (const val of filters.color) {
		const entry = COLORS.find((c) => c.name === val);
		pills.push({
			id: `color-${val}`,
			label: t(`colors.${val}`, { defaultValue: val }),
			colorDot: entry?.dyed
				? "dyed"
				: entry?.transparent
					? "transparent"
					: (entry?.hex ?? undefined),
			onRemove: () =>
				onChange({ color: filters.color.filter((v) => v !== val) }),
		});
	}

	for (const val of filters.shipsTo) {
		pills.push({
			id: `shipsTo-${val}`,
			label: t(`shipsToOptions.${val}`, { defaultValue: val }),
			onRemove: () =>
				onChange({ shipsTo: filters.shipsTo.filter((v) => v !== val) }),
		});
	}

	for (const val of filters.country) {
		pills.push({
			id: `country-${val}`,
			label: val,
			onRemove: () =>
				onChange({ country: filters.country.filter((v) => v !== val) }),
		});
	}

	for (const val of filters.plasticType) {
		pills.push({
			id: `plasticType-${val}`,
			label: val,
			onRemove: () =>
				onChange({ plasticType: filters.plasticType.filter((v) => v !== val) }),
		});
	}

	if (filters.priceMin !== "" || filters.priceMax !== "") {
		pills.push({
			id: "price-range",
			label: priceRangeLabel(filters.priceMin, filters.priceMax),
			onRemove: () => onChange({ priceMin: "", priceMax: "" }),
		});
	}

	if (filters.weightMin !== "" || filters.weightMax !== "") {
		pills.push({
			id: "weight-range",
			label: weightRangeLabel(filters.weightMin, filters.weightMax),
			onRemove: () => onChange({ weightMin: "", weightMax: "" }),
		});
	}

	if (filters.manufacturerIds.length > 0) {
		const count = filters.manufacturerIds.length;
		pills.push({
			id: "manufacturers",
			label: `${count} ${t("activePills.manufacturer", { count })}`,
			onRemove: () => onChange({ manufacturerIds: [] }),
		});
	}

	if (filters.discModelIds.length > 0) {
		const count = filters.discModelIds.length;
		pills.push({
			id: "disc-models",
			label: `${count} ${t("activePills.discModel", { count })}`,
			onRemove: () => onChange({ discModelIds: [] }),
		});
	}

	if (filters.speedMin !== "" || filters.speedMax !== "") {
		pills.push({
			id: "speed-range",
			label: flightRangeLabel(
				t("flightNumbers.speed"),
				filters.speedMin,
				filters.speedMax,
			),
			onRemove: () => onChange({ speedMin: "", speedMax: "" }),
		});
	}
	if (filters.glideMin !== "" || filters.glideMax !== "") {
		pills.push({
			id: "glide-range",
			label: flightRangeLabel(
				t("flightNumbers.glide"),
				filters.glideMin,
				filters.glideMax,
			),
			onRemove: () => onChange({ glideMin: "", glideMax: "" }),
		});
	}
	if (filters.turnMin !== "" || filters.turnMax !== "") {
		pills.push({
			id: "turn-range",
			label: flightRangeLabel(
				t("flightNumbers.turn"),
				filters.turnMin,
				filters.turnMax,
				signedVal,
			),
			onRemove: () => onChange({ turnMin: "", turnMax: "" }),
		});
	}
	if (filters.fadeMin !== "" || filters.fadeMax !== "") {
		pills.push({
			id: "fade-range",
			label: flightRangeLabel(
				t("flightNumbers.fade"),
				filters.fadeMin,
				filters.fadeMax,
			),
			onRemove: () => onChange({ fadeMin: "", fadeMax: "" }),
		});
	}

	if (filters.distanceKm !== "") {
		pills.push({
			id: "distance",
			label: `≤ ${filters.distanceKm} km`,
			onRemove: () => onChange({ distanceKm: "" }),
		});
	}

	if (pills.length === 0) return null;

	return (
		<div className="flex flex-wrap gap-1.5">
			{pills.map(({ id, ...rest }) => (
				<Pill key={id} {...rest} />
			))}
		</div>
	);
}
